// @ts-strict-ignore
import type {
  Action,
  Attribute,
  DataRoute,
  List,
  Outcome,
  PublicId,
  PublishedAttribute,
  ReasonCodeGroup,
  ServiceAttributesById,
  ServiceObject,
  Tag,
  Threshold,
} from 'flow/store/engine/types';
import {
  getAllThresholdFromRules,
} from '../thresholds';
import { snippetMode, snippetOperation } from 'flow/store/redux/types';
import type { SnippetMode, SnippetOperation } from 'flow/store/redux/types';
import { SNIPPET_SCOPE } from './types';
import type {
  SnippetVersion,
  SnippetIntegration,
  SnippetMetadata,
  SnippetScope,
} from './types';
import { getDataRoutesForActions } from '../dataRoutes';
import {
  indexActionsByPublicId,
  isActionOfType,
} from '../actions';
import { ActionType } from 'flow/store/engine/constants';
import {
  getAttributesForServices,
  indexServiceAttributesById,
  indexServicesByInternalName,
  isServicesASubsetOfServicesB,
} from '../services';
import type { WorkflowState } from 'flow/store/redux/state';
import { incompatibleGlobalSnippetActionTypes, incompatibleLocalSnippetActionTypes } from './constants';
import { modifyRelevantDataFromActionRemoval } from 'flow/store/engine/update/actions';
import { deleteProperty } from 'dot-prop';
import { scrubServiceActions, scrubServiceCredentials, scrubServiceDefaultValues } from 'flow/editor/components/SnippetCopyPaste/helpers';
import {
  getListId,
  isListAction,
} from '../actions/services/lists';
import {
  getCustomListToken,
  isCustomListAction,
} from '../actions/services/customLists';
import type { CustomList } from '../actions/services/customLists';
import {
  generateRandomizedIdPublicIdMap, invertPublicIdMap, remapIds,
} from 'flow/service/publicIds/regenerateIds';
import {
  isChildOfSnippetAction,
} from '../actions/snippets';
import { libraryRoutes } from '~/helpers/api-new/modules/library/libraryRoutes';
import type { ServiceConfigOption } from '~/dashboard/views/settings/service-configuration/types';

export function getIsInSnippetReviewMode(mode: SnippetMode | null) {
  return mode === snippetMode.review;
}

export function getIsInSnippetPreviewMode(mode: SnippetMode | null) {
  return mode === snippetMode.preview;
}

export function getIsInSnippetPastePendingMode(mode: SnippetMode | null) {
  return mode === snippetMode.pastePending;
}

export function getIsLinkedSnippetUpgradeOp(operation: SnippetOperation | null) {
  return operation === snippetOperation.upgradeLinkedSnippet;
}

/**
 * Determines the snippet scope based on the URL pathname.
 */
export function getSnippetScopeFromPath(pathname: string, search: string): SnippetScope | null {
  if (pathname.includes(libraryRoutes.localSnippetsView)) return SNIPPET_SCOPE.LOCAL;
  if (pathname.includes(libraryRoutes.globalRepository.view)) {
    const scope = new URLSearchParams(search).get('scope');
    if (scope === 'internal') return SNIPPET_SCOPE.INTERNAL;
    if (scope === 'global') return SNIPPET_SCOPE.GLOBAL;
  }
  return null;
}

/**
 * The editor will only have the compatible services for the given workflow type in its props.
 * This filtered data is returned from the existing Daffy GET endpoint, filtered at the DB level
 * by the query at `Alloy/Daffy/compliance/applications/versions/sql.py#SELECT_SERVICES`.
 * Hence, it is sufficient to check if all of the snippet services are included in the workflow's
 * to determine if the snippet is compatible, without needing to account for the snippet and
 * workflow type.
 */
export function doesSnippetContainIncompatibleServices(args: {
  snippet: SnippetVersion;
  accountServices: ServiceObject[];
}): boolean {
  const { accountServices, snippet } = args;

  const areAllSnippetServicesPresentInWorkflow = isServicesASubsetOfServicesB({
    servicesA: snippet.props.services,
    servicesB: accountServices,
  });

  return !areAllSnippetServicesPresentInWorkflow;
}

/**
 * Finds outcome actions that reference outcomes not present in the provided outcomes list.
 * Returns an array of invalid outcome names, or an empty array if all outcomes are valid.
 */
export function getInvalidOutcomeNames(args: {
  actions: Record<Action['publicId'], Action>;
  outcomes: Outcome[];
}): string[] {
  const { actions, outcomes } = args;
  const validOutcomeNames = new Set(outcomes.map(o => o.outcome));
  const outcomeActions = Object.values(actions).filter(
    action => isActionOfType(action, ActionType.OUTCOME),
  );

  return outcomeActions
    .filter(action => !validOutcomeNames.has(action.outcome?.name))
    .map(action => action.outcome?.name);
}

/**
 * Factory function to create a properly structured SnippetVersion from flat data.
 * Accepts the old flat structure and returns the new nested structure with data/props/metadata.
 */
export function createSnippetVersion(args: {
  actions: Record<Action['publicId'], Action>;
  thresholds: Record<Threshold['publicId'], Threshold>;
  dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  tags: Tag[];
  outcomes: Outcome[];
  services: ServiceObject[];
  publishedAttributes: PublishedAttribute[];
  reasonCodeGroups: ReasonCodeGroup[];
  lists: List[];
  customLists?: CustomList[];
  globalServiceConfigOptions: ServiceConfigOption[];
  universalAttributes: Attribute[];
  snippets?: SnippetMetadata[];
  metadata?: {
    token?: string;
    version?: number;
    integrations?: SnippetIntegration[];
    publishedBy?: string | null;
    publishedAt?: Date | null;
    createdBy?: string | null;
    createdAt?: Date | null;
    changeLog?: string;
    notes?: string;
  } | null;
}): SnippetVersion {
  const {
    actions,
    thresholds,
    dataRoutes,
    tags,
    outcomes,
    services,
    publishedAttributes,
    reasonCodeGroups,
    lists,
    customLists = [],
    globalServiceConfigOptions,
    universalAttributes,
    snippets = [],
    metadata = null,
  } = args;

  return {
    data: {
      actions,
      thresholds,
      dataRoutes,
    },
    props: {
      tags,
      outcomes,
      services,
      publishedAttributes,
      reasonCodeGroups,
      lists,
      customLists,
      globalServiceConfigOptions,
      universalAttributes,
      snippets,
    },
    metadata: metadata ? {
      token: metadata.token ?? '',
      version: metadata.version ?? 0,
      integrations: metadata.integrations ?? [],
      publishedBy: metadata.publishedBy ?? null,
      publishedAt: metadata.publishedAt ?? null,
      createdBy: metadata.createdBy ?? null,
      createdAt: metadata.createdAt ?? null,
      changeLog: metadata.changeLog,
      notes: metadata.notes,
    } : null,
  };
}

/**
 * Returns Record of all connected Actions into `action`
 *@example
 * A -> B -> C
 *   -> D -^
 *
 * For C, the result is:
 * {
 *  [B.publicId]: B,
 *  [D.publicId]: D
 * }
 * todo BUILD-3768 - move this to the actions service and resolve the downstream dependency cycles
 */
export function getIncomerActionsForAction(args: {
  action: Action;
  actions: Record<Action['publicId'], Action>;
  thresholds: Record<Threshold['publicId'], Threshold>;
  dataRoutes: Record<DataRoute['publicId'], DataRoute>;
}): Record<Action['publicId'], Action> {
  const {
    action, actions, thresholds, dataRoutes,
  } = args;

  const result: Record<Action['publicId'], Action> = {};
  const thresholdsForAction = getAllThresholdFromRules({
    rules: action.rules,
    thresholds,
  });

  for (const threshold of thresholdsForAction) {
    const thresholdLeftActionId = threshold.left.actionId;
    const thresholdRightActionId = threshold.right?.actionId;

    if (thresholdLeftActionId && actions[thresholdLeftActionId]) {
      result[thresholdLeftActionId] = actions[thresholdLeftActionId];
    }

    if (thresholdRightActionId && actions[thresholdRightActionId]) {
      result[thresholdRightActionId] = actions[thresholdRightActionId];
    }
  }

  const dataRoutesForAction = getDataRoutesForActions({
    action,
    dataRoutes,
  });

  /**
   * it's possible that an Action has the same incomer in a threshold and in a data route.
   * in that case, this data route lookup would overwrite the action from the thresholds lookup
   * with the same Action, so it's not an issue
   */
  for (const dataRoute of dataRoutesForAction) {
    const sourceActionId = dataRoute.sourceId;

    if (sourceActionId && actions[sourceActionId]) {
      result[sourceActionId] = actions[sourceActionId];
    }
  }

  return result;
}

/**
 * Changes the actionIds in a threshold to the specified action id, if any
 */
export function resolveThresholdParentReferences(args: {
  thresholds: Threshold[];
  actionIdMap: Map<Action['publicId'], Action['publicId']>;
}) {
  const { actionIdMap, thresholds } = args;

  return thresholds.map(threshold => {
    const actionIdToResolve = actionIdMap.get(threshold.actionId);
    const leftActionIdToResolve = actionIdMap.get(threshold.left.actionId);
    const rightActionIdToResolve = actionIdMap.get(threshold.right?.actionId);

    // do not resolve the threshold if it does not point to any merged actions
    if (!leftActionIdToResolve && !rightActionIdToResolve && !actionIdToResolve) {
      return threshold;
    }

    const updatedThreshold = structuredClone(threshold);

    if (actionIdToResolve) {
      updatedThreshold.actionId = actionIdToResolve;
    }

    if (leftActionIdToResolve) {
      updatedThreshold.left.actionId = leftActionIdToResolve;
    }

    if (rightActionIdToResolve) {
      updatedThreshold.right.actionId = rightActionIdToResolve;
    }

    return updatedThreshold;
  });
}

export function resolveDataRouteSourceReferences(args: {
  actionIdMap: Map<Action['publicId'], Action['publicId']>;
  dataRoutes: DataRoute[];
}) {
  const { actionIdMap, dataRoutes } = args;

  return dataRoutes.map(dataRoute => {
    const sourceIdToResolve = actionIdMap.get(dataRoute.sourceId);

    // do not resolve the dataRoute if it does not point to any merged actions
    if (!sourceIdToResolve) {
      return dataRoute;
    }

    const updatedDataRoute = structuredClone(dataRoute);
    updatedDataRoute.sourceId = sourceIdToResolve;
    return updatedDataRoute;
  });
}


/**
 * Returns a copy of the snippet with its actions sorted in a valid topological order
 * todo: consider moving the internal function to the Actions service
 */
export function sortSnippetInTopologicalOrder(args: {
  readonly snippet: SnippetVersion;
}): SnippetVersion {
  /**
   * The algorithm is depth-first search to count the most ancestors an action has in all of its
   * ancestor paths, and sorting the actions within the snippet by that ancestor count in ASC order
   */

  const { snippet } = args;

  // counts the max number of ancestors an action has and stores the count in a counter Map
  function countAncestorActions(action: Action, ancestorCounter: Map<Action['publicId'], number>) {
    // memoization to prevent computing an already-visited action
    if (ancestorCounter.has(action.publicId)) {
      return ancestorCounter.get(action.publicId);
    }

    const incomerActions = getIncomerActionsForAction({
      action,
      actions: snippet.data.actions,
      thresholds: snippet.data.thresholds,
      dataRoutes: snippet.data.dataRoutes,
    });

    const hasIncomers = Object.keys(incomerActions).length > 0;
    if (!hasIncomers) {
      const baseCaseResult = 0;
      ancestorCounter.set(action.publicId, baseCaseResult);
      return 0;
    }

    let maxAncestorCount = 0;
    for (const incomerAction of Object.values(incomerActions)) {
      const ancestorCount = countAncestorActions(incomerAction, ancestorCounter) + 1;
      maxAncestorCount = Math.max(maxAncestorCount, ancestorCount);
    }

    ancestorCounter.set(action.publicId, maxAncestorCount);
    return maxAncestorCount;
  }

  const actionAncestorCounter = new Map<Action['publicId'], number>();
  for (const action of Object.values(snippet.data.actions)) {
    // count the max ancestor (incomer) path for every action in the snippet
    countAncestorActions(action, actionAncestorCounter);
  }


  // sort actions based on how many ancestors they have, in ascending order
  const sortedActionsArray = Object.values(snippet.data.actions).sort((actionA, actionB) => {
    const ancestorCountForA = actionAncestorCounter.get(actionA.publicId);
    const ancestorCountForB = actionAncestorCounter.get(actionB.publicId);

    if (ancestorCountForA === undefined || ancestorCountForB === undefined) {
      throw new Error('something went wrong while sorting snippet');
    }
    return ancestorCountForA - ancestorCountForB;
  });

  const sortedActions = Object.fromEntries(
    sortedActionsArray.map(action => [action.publicId, action]),
  );

  return createSnippetVersion({
    actions: sortedActions,
    dataRoutes: snippet.data.dataRoutes,
    thresholds: snippet.data.thresholds,
    outcomes: snippet.props.outcomes,
    services: snippet.props.services,
    tags: snippet.props.tags,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: snippet.props.lists,
    customLists: snippet.props.customLists,
    globalServiceConfigOptions: snippet.props.globalServiceConfigOptions,
    universalAttributes: snippet.props.universalAttributes ?? [],
    metadata: snippet.metadata,
    snippets: snippet.props.snippets,
  });
}
/**
 * Returns snippet services with only the attributes + service config options actually used
 * in the workflow. Does not scrub secrets/defaults - callers are responsible for scrubbing
 * services (via scrubServiceDefaultValues) before pruning, if needed.
 *
 * TODO - BUILD-4589 - does not carry over customer/alloy default option values, since
 * these may not apply/resolve correctly in a different account. Update once copy/paste
 * materializes non-secret defaults into portable workflow-level action options and
 * resolves them against the target account's own defaults.
 */
function pruneServices({
  services,
  attributeTokens,
  serviceConfigIds,
}: {
  services: ServiceObject[];
  attributeTokens: Set<string>;
  serviceConfigIds: Set<number>;
}): {
    prunedServices: ServiceObject[];
  } {
  const prunedServices = services.map(service => {
    const filteredAttributes = getAttributesForServices(service, { includeOnly: attributeTokens });
    const filteredServiceConfigOptions = (service.serviceConfig?.serviceConfigOptions ?? [])
      .filter(sco => serviceConfigIds.has(sco.serviceConfigOptionId));
    return {
      ...service,
      attributes: indexServiceAttributesById(filteredAttributes),
      serviceConfig: {
        ...service.serviceConfig,
        serviceConfigOptions: filteredServiceConfigOptions,
      },
    };
  });
  return {
    prunedServices,
  };
}

function pruneServicelessProps({
  attributeTokens,
  globalServiceConfigOptions,
  serviceConfigIds,
  universalAttributes,
}: {
  attributeTokens: Set<string>;
  globalServiceConfigOptions: ServiceConfigOption[];
  serviceConfigIds: Set<number>;
  universalAttributes: ServiceAttributesById;
}): {
    filteredGlobalServiceConfigOptions: ServiceConfigOption[];
    filteredUniversalAttributes: Attribute[];
  } {
  const filteredGlobalServiceConfigOptions = globalServiceConfigOptions
    .filter(gsco => serviceConfigIds.has(gsco.serviceConfigOptionId));

  const filteredUniversalAttributes = Object.values(universalAttributes)
    .filter(ua => attributeTokens.has(ua.attribute_token));

  return {
    filteredGlobalServiceConfigOptions,
    filteredUniversalAttributes,
  };
}

/**
 * Filters workflow props to only include items that are actually used by actions in the snippet.
 */
export function filterPropsUsedByActions({
  actions,
  thresholds,
  dataRoutes,
  workflowProps,
}: {
  actions: WorkflowState['workflowData']['actions'];
  thresholds: WorkflowState['workflowData']['thresholds'];
  dataRoutes: WorkflowState['workflowData']['dataRoutes'];
  workflowProps: Pick<
  WorkflowState['workflowProps'],
  'tags' | 'outcomes' | 'services' | 'publishedAttributes' | 'reasonCodeGroups' | 'lists' | 'customLists'
  > & {
    globalServiceConfigOptions?: ServiceConfigOption[];
    universalAttributes?: ServiceAttributesById;
  };
}) {
  const actionsArray = Object.values(actions);
  const thresholdsArray = Object.values(thresholds);
  const dataRoutesArray = Object.values(dataRoutes);

  // Collect identifiers for each prop type used in actions
  const usedServiceInternalNames = new Set<string>();
  const usedAttributeTokens = new Set<string>();
  const usedTagNames = new Set<string>();
  const usedOutcomeNames = new Set<string>();
  const usedPublishedAttributeTokens = new Set<string>();
  const usedReasonCodeGroupNames = new Set<string>();
  const usedServiceConfigOptionIds = new Set<number>();
  // converting list ids to strings since pasted lists w/o conflicts use a token as the listId
  const usedListIds = new Set<string>();
  const usedCustomListTokens = new Set<string>();

  actionsArray.forEach(action => {
    switch (action.type) {
      case ActionType.SERVICE:
        if (action.service?.internalName) {
          usedServiceInternalNames.add(action.service.internalName);
          // extract service config option ids from service action options
          Object.values(action.options).forEach(option => {
            if (option.serviceConfigOptionId) {
              usedServiceConfigOptionIds.add(option.serviceConfigOptionId);
            }
          });
        }
        // list services point to specific customer-owned lists in props - need separate handling
        if (isListAction(action) && getListId(action)) {
          usedListIds.add(String(getListId(action)));
        }
        // custom list services point to specific custom lists in props - need separate handling
        if (isCustomListAction(action) && getCustomListToken(action)) {
          usedCustomListTokens.add(getCustomListToken(action));
        }
        break;

      case ActionType.TAG:
        if (action.tag?.name) {
          usedTagNames.add(action.tag.name);
        }
        break;

      case ActionType.OUTCOME:
        if (action.outcome?.name) {
          usedOutcomeNames.add(action.outcome.name);
        }
        break;

      case ActionType.INPUT:
        if (action.input?.publishedAttributeToken) {
          usedPublishedAttributeTokens.add(action.input.publishedAttributeToken);
        }
        break;

      case ActionType.OUTPUT:
        if (action.output?.publishedAttributeToken) {
          usedPublishedAttributeTokens.add(action.output.publishedAttributeToken);
        }
        break;

      default:
        break;
    }
  });

  thresholdsArray.forEach(threshold => {
    if (threshold.right?.reasonCodeGroup) {
      usedReasonCodeGroupNames.add(threshold.right.reasonCodeGroup.name);
    }
    if (threshold.left?.attributeToken) {
      usedAttributeTokens.add(threshold.left.attributeToken);
    }
    if (threshold.right?.attributeToken && threshold.right.actionId) {
      usedAttributeTokens.add(threshold.right.attributeToken);
    }
  });

  dataRoutesArray.forEach(dataRoute => {
    if (dataRoute.attribute?.token) {
      usedAttributeTokens.add(dataRoute.attribute.token);
    }
  });

  const servicesInWorkflow = Object.values(workflowProps.services).filter(
    service => usedServiceInternalNames.has(service.internal_name),
  );
  const {
    globalServiceConfigOptions = [],
    universalAttributes = {},
  } = workflowProps;

  const {
    prunedServices,
  } = pruneServices({
    services: servicesInWorkflow,
    attributeTokens: usedAttributeTokens,
    serviceConfigIds: usedServiceConfigOptionIds,
  });
  const {
    filteredGlobalServiceConfigOptions,
    filteredUniversalAttributes,
  } = pruneServicelessProps({
    attributeTokens: usedAttributeTokens,
    globalServiceConfigOptions,
    serviceConfigIds: usedServiceConfigOptionIds,
    universalAttributes,
  });
  return {
    services: prunedServices,
    globalServiceConfigOptions: filteredGlobalServiceConfigOptions,
    tags: workflowProps.tags.filter(
      tag => usedTagNames.has(tag.tag_name),
    ),
    outcomes: workflowProps.outcomes.filter(
      outcome => usedOutcomeNames.has(outcome.outcome),
    ),
    publishedAttributes: workflowProps.publishedAttributes.filter(
      pa => usedPublishedAttributeTokens.has(pa.attributeToken),
    ),
    reasonCodeGroups: workflowProps.reasonCodeGroups.filter(
      rcg => usedReasonCodeGroupNames.has(rcg.name),
    ),
    lists: workflowProps.lists.filter(
      list => usedListIds.has(String(list.customer_list_id)),
    ),
    customLists: workflowProps.customLists.filter(
      customList => usedCustomListTokens.has(customList.custom_list_token),
    ),
    universalAttributes: filteredUniversalAttributes,
  };
}

export function isActionSnippetCompatible(action: Action) {
  const isLocalSnippet = getSnippetScopeFromPath(
    location.pathname,
    location.search,
  ) === SNIPPET_SCOPE.LOCAL;
  if (isLocalSnippet) {
    return !isActionOfType(action, incompatibleLocalSnippetActionTypes);
  }
  return !isActionOfType(action, incompatibleGlobalSnippetActionTypes);
}

export function canCopyAction(action: Action) {
  return !isActionOfType(action, ActionType.PREVIEW);
}

/** Returns the actions within a snippet that fail the supplied test. */
export function getIncompatibleActions(args: {
  snippet: SnippetVersion;
  test: (action: Action) => boolean;
}): Action[] {
  const { snippet: { data: { actions } }, test } = args;

  return (
    Object.values(actions).filter(action => !test(action))
  );
}

export function pruneSnippetVersion(args: {
  snippet: SnippetVersion;
  actionsToPrune: Action[];
}): SnippetVersion {
  const { snippet, actionsToPrune } = args;

  const prunedData = structuredClone(snippet.data);
  actionsToPrune.forEach(action => {
    deleteProperty(prunedData.actions, action.publicId);
    modifyRelevantDataFromActionRemoval({
      originalTargetValue: action,
      workflowData: prunedData,
    });
  });

  const prunedProps = filterPropsUsedByActions({
    actions: prunedData.actions,
    thresholds: prunedData.thresholds,
    dataRoutes: prunedData.dataRoutes,
    workflowProps: {
      ...snippet.props,
      services: indexServicesByInternalName(snippet.props.services),
    },
  });

  return createSnippetVersion({
    ...prunedData,
    ...prunedProps,
    metadata: snippet.metadata,
  });
}

/** Scrubs out secrets from the snippet */
export function scrubSnippetVersion({
  snippet,
  globalServiceConfigOptions,
}: {
  snippet: SnippetVersion;
  globalServiceConfigOptions: ServiceConfigOption[];
}) {
  const services = indexServicesByInternalName(snippet.props.services);
  // TODO - BUILD-4737 - test to make sure this can be safely removed
  const {
    actionsWithNoCredentials,
    impactedServiceActions,
  } = scrubServiceCredentials({
    actions: structuredClone(snippet.data.actions),
    services,
  });

  const scrubbedActions = scrubServiceActions({
    actions: actionsWithNoCredentials,
    services,
    globalServiceConfigOptions,
  });

  const scrubbedServices = scrubServiceDefaultValues(snippet.props.services);

  return {
    impactedServiceActions,
    snippet: createSnippetVersion({
      ...snippet.data,
      ...snippet.props,
      actions: scrubbedActions,
      services: scrubbedServices,
      metadata: snippet.metadata,
    }),
  };
}

/**
 * Returns workflow data where all data unrelated to the action and its upstream ancestors is filtered out.
 * This can be considered a subgraph of the workflow graph.
 */
export function getUpstreamSubgraphFromAction(
  workflowData: WorkflowState['workflowData'],
  actionId: Action['publicId'],
): WorkflowState['workflowData'] {
  const { actions, thresholds, dataRoutes } = workflowData;

  // add all actions that is an incomer to the action identified
  // by the supplied action id using BFS

  const queue: Action[] = [actions[actionId]];
  const subgraphActions: Record<Action['publicId'], Action> = {};

  for (const action of queue) {
    subgraphActions[action.publicId] = action;

    const incomerActions = getIncomerActionsForAction({
      action,
      actions,
      thresholds,
      dataRoutes,
    });

    Object.values(incomerActions).forEach(incomerAction => {
      // only visit each action once
      if (!subgraphActions[incomerAction.publicId]) {
        queue.push(incomerAction);
      }
    });
  }

  // filter all thresholds that do not belong to any of the subgraph actions
  const subgraphThresholds: Record<Threshold['publicId'], Threshold> = {};
  Object.values(thresholds).forEach(threshold => {
    if (subgraphActions[threshold.actionId]) {
      subgraphThresholds[threshold.publicId] = threshold;
    }
  });

  // filter all data routes that are not included in subgraph actions.
  const subgraphDataRoutes: Record<DataRoute['publicId'], DataRoute> = {};
  Object.values(dataRoutes).forEach(dataRoute => {
    if (subgraphActions[dataRoute.targetId]) {
      subgraphDataRoutes[dataRoute.publicId] = dataRoute;
    }
  });

  return {
    ...workflowData,
    actions: subgraphActions,
    thresholds: subgraphThresholds,
    dataRoutes: subgraphDataRoutes,
  };
}


export function indexSnippetsMetadataByToken(
  snippetsMetadata: SnippetMetadata[],
): Record<SnippetMetadata['token'], SnippetMetadata> {
  return Object.fromEntries(
    snippetsMetadata.map(snippetMetadata => [snippetMetadata.token, snippetMetadata]),
  );
}


export function regenerateLinkedSnippetsPublicIds(args: {
  snippet: SnippetVersion;
  remoteToLocalPublicIdOverrides?: Record<PublicId, PublicId>;
}) {
  const { snippet, remoteToLocalPublicIdOverrides } = args;

  let remoteToLocalPublicIds = generateRandomizedIdPublicIdMap(args.snippet);
  if (remoteToLocalPublicIdOverrides) {
    // override any ids included in the overrides
    remoteToLocalPublicIds = {
      ...remoteToLocalPublicIds,
      ...remoteToLocalPublicIdOverrides,
    };
  }

  const remappedSnippet = remapIds({ data: snippet, idMap: remoteToLocalPublicIds });

  // invert the index
  const localToRemotePublicIds = invertPublicIdMap(remoteToLocalPublicIds);

  return {
    localToRemotePublicIds,
    remoteToLocalPublicIds,
    snippet: remappedSnippet,
  };
}

export function getSnippetChildActions(args: {
  actions: Record<PublicId, Action>;
  snippetParentActionPublicId: PublicId;
}): Record<PublicId, Action> {
  const { actions, snippetParentActionPublicId } = args;

  return indexActionsByPublicId(Object.values(actions)
    .filter(action => (
      isChildOfSnippetAction({ action, snippetParentActionPublicId })
    )));
}
